import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AccountSecurity = ({ user, onSecurityUpdate }) => {
  const [showTwoFactor, setShowTwoFactor] = useState(false);

  const securityFeatures = [
    {
      title: 'Two-Factor Authentication',
      description: 'Add an extra layer of security to your account',
      icon: 'Smartphone',
      status: user?.twoFactorEnabled ? 'enabled' : 'disabled',
      action: () => setShowTwoFactor(!showTwoFactor)
    },
    {
      title: 'Login Notifications',
      description: 'Get notified when someone logs into your account',
      icon: 'Bell',
      status: user?.loginNotifications ? 'enabled' : 'disabled',
      action: () => onSecurityUpdate('loginNotifications', !user?.loginNotifications)
    },
    {
      title: 'Session Management',
      description: 'View and manage your active sessions',
      icon: 'Monitor',
      status: 'active',
      action: () => console.log('Manage sessions')
    }
  ];

  const recentLogins = [
    {
      device: 'Chrome on Windows',
      location: 'New York, US',
      timestamp: '2025-01-16 08:30:00',
      current: true
    },
    {
      device: 'Safari on iPhone',
      location: 'New York, US',
      timestamp: '2025-01-15 14:22:00',
      current: false
    },
    {
      device: 'Firefox on MacOS',
      location: 'Boston, US',
      timestamp: '2025-01-14 09:15:00',
      current: false
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'enabled':
        return 'text-success';
      case 'disabled':
        return 'text-warning';
      case 'active':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'enabled':
        return 'CheckCircle';
      case 'disabled':
        return 'AlertCircle';
      case 'active':
        return 'Activity';
      default:
        return 'Circle';
    }
  };

  const formatLoginTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  return (
    <div className="bg-card rounded-xl shadow-elevated border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <Icon name="Lock" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-heading font-semibold text-foreground">
          Account Security
        </h2>
      </div>
      {/* Security Features */}
      <div className="space-y-4 mb-8">
        <h3 className="text-lg font-medium text-foreground">Security Settings</h3>
        
        {securityFeatures?.map((feature, index) => (
          <div key={index} className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors duration-250">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                <Icon name={feature?.icon} size={20} color="var(--color-foreground)" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground">
                  {feature?.title}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {feature?.description}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className={`flex items-center space-x-1 ${getStatusColor(feature?.status)}`}>
                <Icon name={getStatusIcon(feature?.status)} size={16} />
                <span className="text-xs font-medium capitalize">
                  {feature?.status}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={feature?.action}
                iconName="Settings"
                iconSize={14}
              >
                Configure
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Recent Login Activity */}
      <div className="border-t border-border pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-foreground">Recent Login Activity</h3>
          <Button variant="ghost" size="sm" iconName="RefreshCw" iconSize={14}>
            Refresh
          </Button>
        </div>

        <div className="space-y-3">
          {recentLogins?.map((login, index) => (
            <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Monitor" size={16} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {login?.device}
                    {login?.current && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                        Current
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {login?.location} • {formatLoginTime(login?.timestamp)}
                  </p>
                </div>
              </div>
              
              {!login?.current && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="LogOut"
                  iconSize={14}
                  className="text-error hover:text-error/80"
                >
                  Revoke
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Emergency Actions */}
      <div className="border-t border-border pt-6 mt-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Emergency Actions</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            iconName="LogOut"
            iconPosition="left"
            className="text-warning border-warning hover:bg-warning/10"
          >
            Sign Out All Devices
          </Button>
          <Button
            variant="outline"
            iconName="AlertTriangle"
            iconPosition="left"
            className="text-error border-error hover:bg-error/10"
          >
            Report Suspicious Activity
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountSecurity;